import { useSnackbar } from "notistack";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { modelsManagementService } from "../../services/models.service";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  IconButton,
  Tab,
  Tabs,
  Typography,
  styled,
  useTheme,
} from "@mui/material";
import ResponsiveDialogManager from "../../components/dialog/ResponsiveDialogManager";
import GltfLoader from "../../components/configurator/configuratorForms/GltfLoader";
import { Add, Check } from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import { Edit, Delete } from "@mui/icons-material";
import ConfirmationDialog from "../../components/dialog/ConfirmationDialog";
import ThemesMapForm from "./ThemesMapForm";
import { myThemesServices } from "../../services";
// import ObjectsGeoloc from "./ObjectsGeoloc";

const PREFIX = "ModelsManagement";

const classes = {
  root: `${PREFIX}-root`,
  header: `${PREFIX}-header`,
  buttonSpaces: `${PREFIX}-buttonSpaces`,
};

const ModelsManagementContainer = styled("div")(({ theme }) => {
  return {
    [`& .${classes.root}`]: {
      margin: "1rem",
    },

    [`& .${classes.header}`]: {
      marginBottom: "2rem",
    },

    [`& .${classes.buttonSpaces}`]: {
      backgroundColor: "#424242",
      color: theme.palette.primary.main,
      marginRight: "15px",
      "&:hover": {
        backgroundColor: "rgba(66,66,66,0.7)",
        color: theme.palette.primary.main,
      },
    },
  };
});

function a11yProps(index) {
  return {
    id: `full-width-tab-${index}`,
    "aria-controls": `full-width-tabpanel-${index}`,
  };
}

const TabPanel = React.forwardRef((props, ref) => {
  const { children, value, index, ...other } = props;

  return (
    <Box ref={ref}>
      <div
        role="tabpanel"
        hidden={value !== index}
        id={`full-width-tabpanel-${index}`}
        aria-labelledby={`full-width-tab-${index}`}
        {...other}
      >
        {value === index && (
          <Box p={3}>
            <div>{children}</div>
          </Box>
        )}
      </div>
    </Box>
  );
});

const ModelsManagement = ({
  myComponents,
  fetchModels,
  fetchThemes,
  loading,
  setLoading,
  setObjectIdToDeleteFromMap,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  // const [models, setModels] = useState(null);
  const [panelValue, setPanelValue] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [currentTheme, setCurrentTheme] = useState(null);
  const [themeDialog, setThemeDialog] = useState(false);
  const [currentModel, setCurrentModel] = useState(null);
  const [modelToDelete, setModelToDelete] = useState(null);
  const [themeToDelete, setThemeToDelete] = useState(null);
  const [saving, setSaving] = useState({
    lock: true,
    trigger: false,
  });

  const deleteTheme = () => {
    setLoading(true);
    myThemesServices
      .deleteTheme(themeToDelete._id)
      .then((res) => {
        enqueueSnackbar(t("toasts.deleteThemeInManagementSuccess"), {
          variant: "success",
        });
        setObjectIdToDeleteFromMap(themeToDelete._id);
        fetchThemes();
      })
      .catch((err) => {
        if (err === 403)
          enqueueSnackbar(t("toasts.unauthorizedError"), {
            variant: "error",
          });
        else
          enqueueSnackbar(t("toasts.deleteThemeInManagementError"), {
            variant: "error",
          });
        setLoading(false);
      });
  };

  const deleteModel = () => {
    setLoading(true);
    modelsManagementService
      .deleteModel(modelToDelete._id)
      .then((res) => {
        enqueueSnackbar(t("toasts.deleteModelInManagementSuccess"), {
          variant: "success",
        });
        setObjectIdToDeleteFromMap(modelToDelete._id);
        fetchModels();
      })
      .catch((err) => {
        if (err === 403)
          enqueueSnackbar(t("toasts.unauthorizedError"), {
            variant: "error",
          });
        else
          enqueueSnackbar(t("toasts.deleteModelInManagementError"), {
            variant: "error",
          });
        setLoading(false);
      });
  };

  const columns = (t, isThemes = false) => [
    // { field: "_id", headerName: "Id", width: 70, type: "string", hide: true },
    {
      field: "previewUrl",
      headerName: "Preview",
      width: 120,
      renderCell: (params) => (
        <img src={params.row.previewUrl} height={50} alt="model preview" />
      ),
    },
    {
      field: isThemes ? "title" : "name",
      headerName: t("modelsManager.nameInGrid"),
      width: 250,
    },
    {
      field: "action",
      headerName: "Actions",
      width: 130,
      renderCell: (params) => (
        <Box>
          <IconButton
            variant="contained"
            color="primary"
            size="small"
            onClick={() => {
              if (isThemes) setCurrentTheme(params.row);
              else setCurrentModel(params.row);
            }}
          >
            <Edit />
          </IconButton>
          <IconButton
            variant="contained"
            color="primary"
            size="small"
            onClick={() => {
              if (isThemes) setThemeToDelete(params.row);
              else setModelToDelete(params.row);
            }}
          >
            <Delete />
          </IconButton>
        </Box>
      ),
    },
  ];

  const handleCreateTheme = () => {
    fetchThemes();
    setCurrentTheme(null);
    setThemeDialog(false);
  };

  const handleChange = (event, newValue) => {
    setPanelValue(newValue);
  };

  if (!myComponents && loading)
    return (
      <Container>
        <Box className="loading-container">
          <CircularProgress size={50} />
          <Typography>{t("loaders.fetching")}</Typography>
        </Box>
      </Container>
    );
  else if (!myComponents && !loading)
    return (
      <Container>
        <Box className="error-container">
          <Typography>{t("toasts.unknownError")}</Typography>
        </Box>
      </Container>
    );
  else
    return (
      <ModelsManagementContainer>
        <ResponsiveDialogManager
          title={
            themeDialog
              ? t("modelsManager.modelManagementDialogTitleTheme")
              : t("modelsManager.modelManagementDialogTitle")
          }
          fullScreen
          open={
            themeDialog || currentTheme || openDialog || currentModel
              ? true
              : false
          }
          onClose={() => {
            setThemeDialog(false);
            setOpenDialog(false);
            setCurrentTheme(null);
            setCurrentModel(null);
            setSaving({ ...saving, trigger: false });
          }}
          renderer={
            themeDialog || currentTheme ? (
              <ThemesMapForm
                currentTheme={currentTheme}
                handleCreateTheme={handleCreateTheme}
              />
            ) : (
              <GltfLoader
                modelManagement
                modelSaveOptions={saving}
                setLockSave={setSaving}
                fetchModels={fetchModels}
                setOpenDialog={setOpenDialog}
                currentModel={currentModel}
                setCurrentModel={setCurrentModel}
                displayAll
              />
            )
          }
          toolbarActions={
            themeDialog || currentTheme ? null : (
              <ModelsManagementContainer>
                <IconButton
                  className={classes.buttonSpaces}
                  disabled={saving.lock || saving.trigger}
                  onClick={() => setSaving({ ...saving, trigger: true })}
                >
                  {saving.trigger ? (
                    <CircularProgress size={28} sx={{ color: "#343434" }} />
                  ) : (
                    <Check />
                  )}
                </IconButton>
              </ModelsManagementContainer>
            )
          }
        />
        <Box className={classes.root}>
          <Typography variant="h4">{t("modelsManager.title")}</Typography>
          <Tabs
            value={panelValue}
            onChange={handleChange}
            indicatorColor="primary"
            textColor="primary"
            variant="fullWidth"
            aria-label="components management tabs"
          >
            <Tab label={t("modelsManager.modelsTabTitle")} {...a11yProps(0)} />
            <Tab label={t("modelsManager.themesTabTitle")} {...a11yProps(1)} />
          </Tabs>
          <TabPanel value={panelValue} index={0} dir={theme.direction}>
            <Box sx={{ margin: "1rem 0" }}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => setOpenDialog(true)}
                sx={{ marginBottom: "1rem" }}
                startIcon={<Add />}
              >
                {t("modelsManager.addModelButton")}
              </Button>

              <DataGrid
                rows={myComponents.objects}
                columns={columns(t)}
                autoHeight
                disableRowSelectionOnClick
                pagination
                loading={loading}
                getRowId={(row) => row._id}
                initialState={{
                  pagination: { paginationModel: { pageSize: 10 } },
                }}
                pageSizeOptions={[5, 10, 25, 50]}
              />
            </Box>
          </TabPanel>
          <TabPanel value={panelValue} index={1} dir={theme.direction}>
            <Box sx={{ margin: "1rem 0" }}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => setThemeDialog(true)}
                startIcon={<Add />}
                sx={{ marginBottom: "1rem" }}
              >
                {t("modelsManager.addThemeButton")}
              </Button>
              <DataGrid
                rows={myComponents.themes}
                columns={columns(t, true)}
                autoHeight
                disableRowSelectionOnClick
                pagination
                loading={loading}
                getRowId={(row) => row._id}
                initialState={{
                  pagination: { paginationModel: { pageSize: 10 } },
                }}
                pageSizeOptions={[5, 10, 25, 50]}
              />
            </Box>
          </TabPanel>
        </Box>
        <ConfirmationDialog
          open={modelToDelete || themeToDelete ? true : false}
          onAgree={modelToDelete ? deleteModel : deleteTheme}
          element={modelToDelete || themeToDelete || null}
          setOpen={modelToDelete ? setModelToDelete : setThemeToDelete}
          mode={modelToDelete ? "deleteModel" : "deleteTheme"}
        />
      </ModelsManagementContainer>
    );
};

export default ModelsManagement;
